/**
 * Live comment counts for the per-post Viafoura preview cards.
 *
 * Viafoura's own `<vf-conversations-count>` element is not usable here. It only renders once their
 * main bundle is on the page, and the entire loader design turns on NOT fetching cdn.viafoura.net
 * until a reader opens a conversation (design D15). Their bulk count endpoint carries no such cost:
 * it is a plain JSON API, needs no bundle, and answers for every post on the page in one request —
 * which is the shape a liveblog needs. Viafoura's own documentation points listing pages at this
 * endpoint rather than the widget for exactly that reason.
 *
 * Two layers, and they solve different problems:
 *
 *   react-query  — caching, de-duplication, staleness and the refresh interval, exactly as the
 *                  native comment preview uses it (`useChannelPreview`). Every card is its own
 *                  query, so N cards showing the SAME container share one cache entry and one
 *                  in-flight request for free, and remounting a card that scrolled out of view is
 *                  a cache read rather than a request.
 *   batch loader — react-query de-duplicates identical keys, but cannot combine *different* ones
 *                  into a single HTTP call, and a liveblog renders hundreds of distinct containers
 *                  at once. The loader below coalesces whatever query functions run in the same
 *                  short window into one POST and hands each caller back its own container's count.
 *
 * So a liveblog with 200 Viafoura posts issues 200 cached queries over one network request.
 *
 * PRIVACY NOTE, deliberately different from the conversation loader: this does contact Viafoura at
 * page load, which the loader is specifically built to avoid. It is a cookieless, credential-less
 * GET/POST pair that sends no reader identifier — but it does expose the reader's IP to Viafoura
 * before they have asked for a conversation. Routing it through our own backend instead would
 * restore that property; see the proposal's open questions.
 */

/**
 * Viafoura's site lookup — the same one their widget uses to decide which account a page belongs to.
 */
const SECTION_LOOKUP_BASE = "https://tyrion.viafoura.co/v3/sections/sites/";

const COUNTS_BASE = "https://livecomments.viafoura.co/v4/livecomments/";

/**
 * How long to collect container ids before firing a request.
 *
 * A liveblog mounts its rows in one commit, but the rows that matter here arrive in bursts — page
 * load, "load more", and websocket updates each produce a cluster, and so does react-query firing a
 * page's worth of refetch intervals. Coalescing turns each cluster into a single request; the delay
 * is imperceptible next to the fetch itself.
 */
const BATCH_DELAY_MS = 50;

/**
 * Matches the native comment preview's poll (`useChannelPreview`), so the two providers' counts go
 * stale at the same rate and a page showing both does not look inconsistent.
 */
export const VIAFOURA_COUNT_REFRESH_MS = 60000;

/**
 * Viafoura documents no maximum batch size, so we impose one rather than discovering theirs in
 * production on a liveblog with a thousand posts loaded.
 */
const MAX_CONTAINERS_PER_REQUEST = 100;

/**
 * The one status that means "this host has no Viafoura site". Every other failure is transient and
 * must stay retryable — see `loadSectionUuid`.
 */
const SECTION_NOT_PROVISIONED = 404;

/**
 * The uuid is interpolated into a request path, so it is checked for shape before it gets there. A
 * value carrying `../`, a leading `//host/`, or a `?` would otherwise rewrite the target of the
 * counts POST from whatever the lookup returned.
 */
const SECTION_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface SectionResponse {
  uuid?: string;
}

interface ContainerCountResponse {
  containerId: string;
  commentCount: number;
  status: "visible" | "hidden";
}

/**
 * Which Viafoura account ("site") this page belongs to, or `null` if it belongs to none.
 *
 * Resolved from the page's hostname, exactly as Viafoura's own widget resolves it, rather than from
 * a per-organization setting. That is not a shortcut — as far as Viafoura is concerned the hostname
 * IS the identity, measured in the add-viafoura-conversation-provider change: with the page on one
 * host and `og:url` pointing elsewhere, the site lookup follows the host. A section uuid stored per
 * organization would be a second source of truth that can silently disagree with the one Viafoura
 * actually uses, and disagreeing fails quietly (see `fetchCountsForBatch`).
 *
 * Tickaroo's own domains are deliberately not provisioned with Viafoura, so this resolves to `null`
 * in the editor preview and on the hosted fullpage. No counts appear there, which is the right
 * outcome for surfaces whose triggers are disabled anyway.
 *
 * `null` rather than `undefined` throughout: react-query rejects a query function that resolves to
 * `undefined`.
 */
async function loadSectionUuid(): Promise<string | null> {
  if (typeof window === "undefined") {
    return null;
  }
  const response = await fetch(SECTION_LOOKUP_BASE + encodeURIComponent(window.location.hostname), {
    credentials: "omit"
  });
  // Only this answer is cached forever. A 5xx, a rate limit, an ad blocker or an offline reader all
  // throw instead, because the query caches a resolved `null` with infinite staleness — turning a
  // momentary blip into a page whose counts never appear again until the reader reloads.
  if (response.status === SECTION_NOT_PROVISIONED) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Viafoura section lookup failed: ${response.status}`);
  }
  const section = await response.json() as SectionResponse;
  if (!section.uuid || !SECTION_UUID_PATTERN.test(section.uuid)) {
    // Not an answer either: the lookup 404s for a host it does not know, so a 200 carrying no usable
    // uuid is a malformed or half-recovered response. Caching it forever would be the same page-wide
    // outage as caching a 5xx.
    throw new Error("Viafoura section lookup returned no usable uuid");
  }
  return section.uuid;
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
}

/**
 * One POST per chunk, merged into a single lookup table.
 *
 * Containers Viafoura has never seen are filtered out of the response rather than returned as zero,
 * and they stay absent here rather than becoming zero. The distinction matters because a section
 * uuid that matches nothing returns `[]` with HTTP 200 — indistinguishable from "none of these have
 * conversations". Treating an absent entry as zero would turn that silent failure into every card on
 * the page confidently claiming zero comments.
 */
async function fetchCountsForBatch(sectionUuid: string, containerIds: string[]): Promise<Map<string, number>> {
  const url = `${COUNTS_BASE}${sectionUuid}/content-containers/comment-count-and-status/by-container-ids`;
  const counts = new Map<string, number>();

  await Promise.all(chunk(containerIds, MAX_CONTAINERS_PER_REQUEST).map(async batch => {
    let results: ContainerCountResponse[];
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        // No cookies. Viafoura's endpoint accepts a session cookie to personalise the answer, and we
        // want the public count — sending a logged-in reader's Viafoura session to get a number we
        // would render identically either way is reader data spent for nothing.
        credentials: "omit",
        body: JSON.stringify(batch)
      });
      if (!response.ok) {
        return;
      }
      results = await response.json();
    } catch {
      // Offline or blocked. The other chunks may still have answered.
      return;
    }

    results.forEach(({containerId, commentCount, status}) => {
      // A hidden container is one the publisher has closed or Viafoura has withheld. Its comments
      // are not reachable from our card, so counting them would advertise a conversation the reader
      // cannot get to.
      if (status === "visible") {
        counts.set(containerId, commentCount);
      }
    });
  }));

  return counts;
}

/**
 * The batch currently accepting container ids, if any.
 *
 * Deliberately module state rather than react-query state: this is the one thing react-query cannot
 * do for us, because it de-duplicates identical keys and every card here has a *different* key.
 */
let pendingBatch: {
  sectionUuid: string,
  containerIds: Set<string>,
  counts: Promise<Map<string, number>>
} | undefined;

function joinBatch(sectionUuid: string, containerId: string): Promise<Map<string, number>> {
  // A section change mid-flight cannot happen on a live page (the hostname is fixed), but starting a
  // fresh batch rather than mixing ids across sections keeps that assumption from being load-bearing.
  if (!pendingBatch || pendingBatch.sectionUuid !== sectionUuid) {
    const containerIds = new Set<string>();
    const counts = new Promise<Map<string, number>>(resolve => {
      setTimeout(() => {
        // Stop accepting ids before the request goes out, so a query function that runs while it is
        // in flight opens the next batch instead of missing this one's departure.
        pendingBatch = undefined;
        resolve(fetchCountsForBatch(sectionUuid, Array.from(containerIds)));
      }, BATCH_DELAY_MS);
    });
    pendingBatch = {sectionUuid, containerIds, counts};
  }
  pendingBatch.containerIds.add(containerId);
  return pendingBatch.counts;
}

/**
 * Query function for one container's count: `null` when Viafoura has no answer for it.
 *
 * Every caller in the same window rides the same request; each still gets only its own number, so
 * react-query caches one entry per container rather than a page-wide blob that would invalidate
 * wholesale whenever any single post's count moved.
 */
export async function loadViafouraCommentCount(sectionUuid: string, containerId: string): Promise<number | null> {
  const counts = await joinBatch(sectionUuid, containerId);
  return counts.get(containerId) ?? null;
}

/** Shared cache key root, so the whole feature can be invalidated or cleared in one call. */
export const VIAFOURA_COUNTS_QUERY_KEY = "viafouraCommentCounts";

/**
 * The section lookup as react-query options.
 *
 * One key for the whole page means one lookup however many cards mount — the de-duplication that
 * used to be a hand-rolled cached promise. A resolved answer never goes stale: it cannot change
 * without a navigation.
 *
 * A *failed* lookup is the opposite case and gets the opposite treatment. Every count on the page
 * hangs off this one query, so a cached failure is a page-wide outage lasting until reload; the
 * backoff here, plus react-query's refetch on reconnect, lets a blip resolve itself.
 */
export function viafouraSectionQueryOptions() {
  return {
    queryKey: [VIAFOURA_COUNTS_QUERY_KEY, "section"] as const,
    queryFn: loadSectionUuid,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000)
  };
}

/**
 * One container's count as react-query options.
 *
 * `staleTime` matches the refresh interval so that a card remounting between ticks — a reader
 * scrolling back to a post — reads the cache instead of queueing another request.
 */
export function viafouraCommentCountQueryOptions(sectionUuid: string | null | undefined, containerId: string) {
  return {
    queryKey: [VIAFOURA_COUNTS_QUERY_KEY, "count", sectionUuid, containerId] as const,
    queryFn: () => loadViafouraCommentCount(sectionUuid!, containerId),
    refetchInterval: VIAFOURA_COUNT_REFRESH_MS,
    staleTime: VIAFOURA_COUNT_REFRESH_MS,
    // A count is decoration on a trigger that works without it. Retrying a blocked or unreachable
    // endpoint on every card would multiply a failure into a burst of traffic for no visible gain.
    retry: false
  };
}

/** Test seam — drops any batch still accepting ids. */
export function resetViafouraCommentCountsForTests() {
  pendingBatch = undefined;
}
